/**
 * Layout constants shared across AppLayout components.
 */

export const SIDEBAR_WIDTH = 280;
export const SIDEBAR_COLLAPSED_WIDTH = 80;
export const HEADER_HEIGHT = 64;
export const FOOTER_HEIGHT = 48;

/** TailAdmin-inspired color palette. */
export const COLORS = {
  sidebarBg: '#1C2434',
  sidebarBorder: '#313D4F',
  sidebarHoverBg: '#313D4F',
  navText: '#C8CFD8',
  navTextHover: '#FFFFFF',
  navTextActive: '#FFFFFF',
  sectionHeader: '#DEE4ED',
  accentPurple: '#6B3FA0',
  headerBg: '#FFFFFF',
  headerBorder: '#E2E8F0',
  contentBg: '#F8FAFC',
  chipInactive: '#EDF2F7',
  chipInactiveText: '#64748B',
};

/** Platform functionality definitions. */
export interface FunctionalityDef {
  id: string;
  label: string;
  description: string;
  defaultRoute: string;
}

export const FUNCTIONALITIES: FunctionalityDef[] = [
  {
    id: 'pricing',
    label: 'Products',
    description: 'Insurance Product Pricing',
    defaultRoute: '/pricing',
  },
  {
    id: 'policy_data',
    label: 'Policy Data',
    description: 'Life Policy Member Data & Cohorts',
    defaultRoute: '/policy-data',
  },
  {
    id: 'reinsurance',
    label: 'Reinsurance',
    description: 'Reinsurance Management',
    defaultRoute: '/reinsurance-module',
  },
  {
    id: 'budgeting',
    label: 'Budgeting',
    description: 'Insurance Budgeting & Forecasting',
    defaultRoute: '/budgeting',
  },
  {
    id: 'actuarial_model',
    label: 'Actuarial Model',
    description: 'IFRS 17 Actuarial Modelling',
    defaultRoute: '/actuarial-model',
  },
  {
    id: 'audit_workbook',
    label: 'Audit Workbook',
    description: 'IFRS 17 PAA Online Workbook',
    defaultRoute: '/dashboard',
  },
  {
    id: 'financial_condition_report',
    label: 'Financial Condition Report',
    description: 'Statutory FCR & Solvency Reporting',
    defaultRoute: '/financial-condition-report',
  },
];

export const FUNCTIONALITY_MAP = Object.fromEntries(
  FUNCTIONALITIES.map((f) => [f.id, f]),
);

// ─── Functionality-specific sidebar navigation ─────────────────────

export interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** If set, only users with one of these roles see this item. */
  roles?: string[];
  /** If true, item is only shown when IFRS 4 BS is enabled for the period. */
  ifrs4Only?: boolean;
}

export interface NavSection {
  label: string;
  collapsible?: boolean;
  items: NavItem[];
}

/**
 * Maps each functionality to its sidebar nav sections.
 * Role filtering is applied at render time — items without `roles` are visible to all.
 */
export const FUNCTIONALITY_NAV: Record<string, NavSection[]> = {
  audit_workbook: [
    {
      label: 'Overview',
      items: [
        { to: '/dashboard', label: 'Dashboard', icon: 'Dashboard' },
      ],
    },
    {
      label: 'Data Entry',
      collapsible: true,
      items: [
        // company_user (analyst) data entry
        { to: '/premiums', label: 'Premiums', icon: 'AttachMoney', roles: ['company_user'] },
        { to: '/claims', label: 'Claims', icon: 'Receipt', roles: ['company_user'] },
        { to: '/reinsurance', label: 'Reinsurance', icon: 'CompareArrows', roles: ['company_user'] },
        { to: '/commissions', label: 'Commissions', icon: 'AttachMoney', roles: ['company_user'] },
        {
          to: '/expenses',
          label: 'Mgmt Expenses',
          icon: 'AccountBalance',
          roles: ['company_user'],
        },
        {
          to: '/expense-allocations',
          label: 'Expense Allocations',
          icon: 'PieChart',
          roles: ['company_user'],
        },
        {
          to: '/ifrs4-balance-sheet',
          label: 'IFRS 4 Balance Sheet',
          icon: 'HistoryEdu',
          roles: ['company_user'],
          ifrs4Only: true,
        },
        // consultant_user data entry
        { to: '/upr', label: 'UPR', icon: 'TrendingDown', roles: ['consultant_user'] },
        { to: '/dac', label: 'DAC', icon: 'MoneyOff', roles: ['consultant_user'] },
        {
          to: '/risk-adjustment',
          label: 'Risk Adjustment',
          icon: 'Security',
          roles: ['consultant_user'],
        },
        {
          to: '/retention-rates',
          label: 'Retention Rates',
          icon: 'Percent',
          roles: ['consultant_user'],
        },
        {
          to: '/expense-allocations',
          label: 'Expense Allocations',
          icon: 'PieChart',
          roles: ['consultant_user'],
        },
        { to: '/triangles', label: 'Triangles', icon: 'GridOn', roles: ['consultant_user'] },
        {
          to: '/gmm-data-entry',
          label: 'GMM Data Entry',
          icon: 'AccountTree',
          roles: ['consultant_user'],
        },
        {
          to: '/life-assumptions',
          label: 'Life Assumptions',
          icon: 'GridOn',
          roles: ['consultant_user'],
        },
      ],
    },
    {
      label: 'Data Review',
      collapsible: true,
      items: [
        // Auditor read-only views
        { to: '/provided/premiums', label: 'Premiums', icon: 'Visibility', roles: ['auditor'] },
        { to: '/provided/claims', label: 'Claims', icon: 'Visibility', roles: ['auditor'] },
        {
          to: '/provided/reinsurance',
          label: 'Reinsurance',
          icon: 'Visibility',
          roles: ['auditor'],
        },
        {
          to: '/provided/commissions',
          label: 'Commissions',
          icon: 'Visibility',
          roles: ['auditor'],
        },
        {
          to: '/provided/expenses',
          label: 'Mgmt Expenses',
          icon: 'Visibility',
          roles: ['auditor'],
        },
        {
          to: '/provided/expense-allocations',
          label: 'Expense Allocations',
          icon: 'Visibility',
          roles: ['auditor'],
        },
        { to: '/provided/upr', label: 'UPR', icon: 'Visibility', roles: ['auditor'] },
        { to: '/provided/dac', label: 'DAC', icon: 'Visibility', roles: ['auditor'] },
        {
          to: '/provided/risk-adjustment',
          label: 'Risk Adjustment',
          icon: 'Visibility',
          roles: ['auditor'],
        },
        {
          to: '/provided/retention-rates',
          label: 'Retention Rates',
          icon: 'Visibility',
          roles: ['auditor'],
        },
        {
          to: '/provided/triangles',
          label: 'Triangles',
          icon: 'Visibility',
          roles: ['auditor'],
        },
      ],
    },
    {
      label: 'Data Provided',
      collapsible: true,
      items: [
        // Analyst sees consultant data (read-only)
        { to: '/provided/upr', label: 'UPR', icon: 'Visibility', roles: ['company_user'] },
        { to: '/provided/dac', label: 'DAC', icon: 'Visibility', roles: ['company_user'] },
        {
          to: '/provided/risk-adjustment',
          label: 'Risk Adjustment',
          icon: 'Visibility',
          roles: ['company_user'],
        },
        {
          to: '/provided/retention-rates',
          label: 'Retention Rates',
          icon: 'Visibility',
          roles: ['company_user'],
        },
        {
          to: '/provided/triangles',
          label: 'Triangles',
          icon: 'Visibility',
          roles: ['company_user'],
        },
        // Consultant sees analyst data (read-only)
        {
          to: '/provided/premiums',
          label: 'Premiums',
          icon: 'Visibility',
          roles: ['consultant_user'],
        },
        {
          to: '/provided/claims',
          label: 'Claims',
          icon: 'Visibility',
          roles: ['consultant_user'],
        },
        {
          to: '/provided/reinsurance',
          label: 'Reinsurance',
          icon: 'Visibility',
          roles: ['consultant_user'],
        },
        {
          to: '/provided/commissions',
          label: 'Commissions',
          icon: 'Visibility',
          roles: ['consultant_user'],
        },
        {
          to: '/provided/expenses',
          label: 'Mgmt Expenses',
          icon: 'Visibility',
          roles: ['consultant_user'],
        },
        {
          to: '/provided/expense-allocations',
          label: 'Expense Allocations',
          icon: 'Visibility',
          roles: ['consultant_user'],
        },
        {
          to: '/provided/ifrs4-balance-sheet',
          label: 'IFRS 4 Balance Sheet',
          icon: 'Visibility',
          roles: ['consultant_user'],
          ifrs4Only: true,
        },
      ],
    },
    {
      label: 'Analysis',
      collapsible: true,
      items: [
        {
          to: '/calculations',
          label: 'Calculations',
          icon: 'Calculate',
          roles: ['consultant_user'],
        },
        {
          to: '/life-calculations',
          label: 'Life Calculations',
          icon: 'Calculate',
          roles: ['consultant_user'],
        },
        {
          to: '/approvals',
          label: 'Approvals',
          icon: 'FactCheck',
          roles: ['company_user', 'consultant_user', 'auditor', 'executive'],
        },
        { to: '/disclosures', label: 'Disclosures', icon: 'Article' },
        { to: '/life-disclosures', label: 'Life Disclosures', icon: 'Article' },
        {
          to: '/life-contract-groups',
          label: 'Life Contract Groups',
          icon: 'AccountTree',
          roles: ['company_user', 'consultant_user', 'auditor'],
        },
        {
          to: '/life-reconciliation',
          label: 'Life Reconciliation',
          icon: 'TableChart',
        },
        {
          to: '/life-sensitivity',
          label: 'Life Sensitivity',
          icon: 'ShowChart',
        },
        {
          to: '/life-asset-share',
          label: 'Asset Share (VFA)',
          icon: 'PieChart',
          roles: ['company_user', 'consultant_user', 'auditor'],
        },
        {
          to: '/job-status',
          label: 'Job Status',
          icon: 'MonitorHeart',
          roles: ['super_admin', 'admin', 'consultant_user'],
        },
        {
          to: '/financial-statements',
          label: 'Financial Statements',
          icon: 'AccountBalanceWallet',
        },
        {
          to: '/sensitivity',
          label: 'Sensitivity Analysis',
          icon: 'ShowChart',
          roles: ['company_user', 'consultant_user', 'auditor', 'executive'],
        },
        { to: '/risk-disclosures', label: 'Risk Disclosures', icon: 'Shield' },
        {
          to: '/gmm-disclosures',
          label: 'GMM Disclosures',
          icon: 'TableChart',
          roles: ['company_user', 'consultant_user', 'auditor', 'executive'],
        },
        {
          to: '/ifrs4-conversion',
          label: 'IFRS 4 Conversion',
          icon: 'CompareArrows',
          roles: ['company_user', 'consultant_user', 'auditor'],
          ifrs4Only: true,
        },
      ],
    },
    {
      label: 'Audit',
      items: [
        { to: '/approvals', label: 'Audit Trail', icon: 'FactCheck', roles: ['auditor'] },
      ],
    },
    {
      label: 'Import / Export',
      items: [
        {
          to: '/import-export',
          label: 'Excel Import/Export',
          icon: 'SwapVert',
          roles: ['company_user', 'consultant_user'],
        },
        {
          to: '/bulk-upload',
          label: 'Bulk CSV Upload',
          icon: 'SwapVert',
          roles: ['company_user', 'consultant_user'],
        },
      ],
    },
    {
      label: 'Reporting',
      items: [
        {
          to: '/xbrl',
          label: 'XBRL Reporting',
          icon: 'DataObject',
          roles: ['company_user', 'consultant_user', 'auditor', 'regulator'],
        },
      ],
    },
  ],

  budgeting: [
    {
      label: 'Overview',
      items: [
        { to: '/budget-dashboard', label: 'Dashboard', icon: 'Dashboard' },
      ],
    },
    {
      label: 'Budgeting',
      items: [
        { to: '/budget-assumptions', label: 'Assumptions', icon: 'Calculate' },
        { to: '/budget-variance', label: 'Variance Analysis', icon: 'CompareArrows' },
      ],
    },
  ],

  actuarial_model: [
    {
      label: 'Overview',
      items: [
        { to: '/actuarial-model', label: 'Dashboard', icon: 'Dashboard' },
      ],
    },
    {
      label: 'Model',
      items: [
        { to: '/act-method-selection', label: 'Method Selection', icon: 'Calculate' },
        { to: '/act-stochastic', label: 'Stochastic Reserving', icon: 'ShowChart' },
        { to: '/act-experience', label: 'Experience Analysis', icon: 'TrendingDown' },
      ],
    },
    {
      label: 'Global Assumptions',
      items: [
        { to: '/admin/yield-curves', label: 'Yield Curves', icon: 'ShowChart' },
        { to: '/admin/mortality-tables', label: 'Mortality Tables', icon: 'GridOn' },
      ],
    },
    {
      label: 'Company Assumptions',
      items: [
        { to: '/act-governance', label: 'Assumption Governance', icon: 'Gavel' },
        { to: '/life-assumptions', label: 'Life Assumptions', icon: 'GridOn' },
        {
          to: '/act-active-products',
          label: 'Active Products',
          icon: 'Category',
          roles: ['consultant_user', 'super_admin'],
        },
        {
          to: '/act-linkage',
          label: 'Product Linkages',
          icon: 'Link',
          roles: ['consultant_user', 'super_admin'],
        },
      ],
    },
    {
      label: 'Data',
      items: [{ to: '/triangles', label: 'Triangles', icon: 'GridOn' }],
    },
    {
      label: 'GMM / VFA Model',
      collapsible: true,
      items: [
        {
          to: '/act-gmm-test',
          label: 'GMM Test Runner',
          icon: 'PlayArrow',
          roles: ['consultant_user', 'super_admin', 'admin'],
        },
        {
          to: '/act-gmm-comparison',
          label: 'Results Comparison',
          icon: 'CompareArrows',
          roles: ['consultant_user', 'super_admin', 'admin'],
        },
      ],
    },
    {
      label: 'Results',
      items: [
        { to: '/reserve-results', label: 'Reserve Results', icon: 'AccountBalanceWallet' },
        {
          to: '/calculations',
          label: 'Calculations',
          icon: 'Calculate',
          roles: ['consultant_user'],
        },
        {
          to: '/life-calculations',
          label: 'Life Calculations',
          icon: 'Calculate',
          roles: ['consultant_user'],
        },
        { to: '/life-disclosures', label: 'Life Disclosures', icon: 'Article' },
        {
          to: '/life-contract-groups',
          label: 'Life Contract Groups',
          icon: 'AccountTree',
        },
        {
          to: '/life-reconciliation',
          label: 'Life Reconciliation',
          icon: 'TableChart',
        },
        {
          to: '/life-sensitivity',
          label: 'Life Sensitivity',
          icon: 'ShowChart',
        },
        {
          to: '/life-asset-share',
          label: 'Asset Share (VFA)',
          icon: 'PieChart',
        },
      ],
    },
  ],

  reinsurance: [
    {
      label: 'Overview',
      items: [
        { to: '/reinsurance-module', label: 'Dashboard', icon: 'Dashboard' },
      ],
    },
    {
      label: 'Reinsurance',
      items: [
        { to: '/ri-treaties', label: 'Treaty Management', icon: 'CompareArrows' },
        { to: '/ri-performance', label: 'Performance', icon: 'ShowChart' },
        {
          to: '/ri-adequacy',
          label: 'Adequacy Report',
          icon: 'FactCheck',
          roles: ['super_admin', 'admin', 'consultant_user'],
        },
      ],
    },
  ],

  pricing: [
    {
      label: 'Overview',
      items: [
        { to: '/pricing', label: 'Dashboard', icon: 'Dashboard' },
      ],
    },
    {
      label: 'Active Products',
      items: [
        {
          to: '/active-products',
          label: 'Product Catalog',
          icon: 'Category',
          roles: ['consultant_user', 'super_admin'],
        },
      ],
    },
    {
      label: 'Actuarial Pricing',
      items: [
        { to: '/pricing-products', label: 'Products', icon: 'Category' },
        {
          to: '/pricing-analysis',
          label: 'Analysis',
          icon: 'ShowChart',
          roles: ['super_admin', 'admin', 'consultant_user'],
        },
        {
          to: '/pricing-profit-test',
          label: 'Profit Test',
          icon: 'Calculate',
          roles: ['super_admin', 'admin', 'consultant_user'],
        },
        {
          to: '/pricing-certificates',
          label: 'Certificates',
          icon: 'Article',
          roles: ['super_admin', 'admin', 'consultant_user'],
        },
      ],
    },
    {
      label: 'Life Profit Testing',
      items: [
        {
          to: '/life-pricing',
          label: 'Dashboard',
          icon: 'Dashboard',
          roles: ['super_admin', 'consultant_user'],
        },
        {
          to: '/life-pricing/model-points',
          label: 'Model Points',
          icon: 'People',
          roles: ['super_admin', 'consultant_user'],
        },
        {
          to: '/life-pricing/run',
          label: 'Run Profit Test',
          icon: 'Calculate',
          roles: ['super_admin', 'consultant_user'],
        },
        {
          to: '/life-pricing/history',
          label: 'Saved Results',
          icon: 'History',
          roles: ['super_admin', 'consultant_user'],
        },
      ],
    },
  ],

  policy_data: [
    {
      label: 'Overview',
      items: [
        { to: '/policy-data', label: 'Dashboard', icon: 'Dashboard' },
      ],
    },
    {
      label: 'Data Entry',
      items: [
        {
          to: '/policy-data/upload',
          label: 'Policy Data Input',
          icon: 'CloudUpload',
          roles: ['super_admin', 'admin', 'company_user', 'consultant_user'],
        },
        {
          to: '/policy-data/claims',
          label: 'Claims Data Entry',
          icon: 'Receipt',
          roles: ['super_admin', 'admin', 'company_user', 'consultant_user'],
        },
      ],
    },
    {
      label: 'Reports',
      items: [
        { to: '/policy-data/members', label: 'Member Registry', icon: 'People' },
        { to: '/policy-data/movements', label: 'Membership Movements', icon: 'CompareArrows' },
        { to: '/policy-data/cohort-categories', label: 'Cohort Categories', icon: 'Category' },
        { to: '/policy-data/cohorts', label: 'Cohort Summary', icon: 'TableChart' },
      ],
    },
  ],

  financial_condition_report: [
    {
      label: 'Overview',
      items: [
        { to: '/fcr-dashboard', label: 'Dashboard', icon: 'Dashboard' },
        { to: '/fcr-report', label: 'Full Report', icon: 'Description' },
      ],
    },
    {
      label: 'Report Sections',
      items: [
        { to: '/fcr-report', label: 'Executive Summary', icon: 'Summarize' },
        { to: '/fcr-report', label: 'Business Overview', icon: 'Business' },
        { to: '/fcr-report', label: 'Profitability', icon: 'TrendingUp' },
        { to: '/fcr-report', label: 'Reserves', icon: 'Assessment' },
        { to: '/fcr-report', label: 'Capital Adequacy', icon: 'AccountBalance' },
        { to: '/fcr-report', label: 'Reinsurance', icon: 'Shield' },
        { to: '/fcr-report', label: 'Risk Management', icon: 'Warning' },
        { to: '/fcr-report', label: 'Recommendations', icon: 'Lightbulb' },
      ],
    },
    {
      label: 'Tools',
      items: [
        {
          to: '/fcr-solvency',
          label: 'Solvency Analysis',
          icon: 'AccountBalance',
          roles: ['super_admin', 'admin', 'consultant_user'],
        },
        {
          to: '/fcr-risk-heatmap',
          label: 'Risk Heatmap',
          icon: 'Shield',
          roles: ['super_admin', 'admin', 'consultant_user'],
        },
        {
          to: '/fcr-stress-test',
          label: 'Stress Testing',
          icon: 'ShowChart',
          roles: ['super_admin', 'admin', 'consultant_user'],
        },
      ],
    },
  ],
};
